import { useState } from 'react';
import { Link } from 'react-router';
import { usePairsAtSite, useNearby } from '../lib/queries.ts';
import type { Pair } from '../lib/sl.ts';
import { Button, Card, Disclosure, Field, LineBadge } from './ui.tsx';

/**
 * "A line I know serves this stop is not in the list."
 *
 * Two things cause that, and this offers a way out of each.
 *
 * Most often the line is real but the stop is not the one you meant: SL
 * splits a place into more sites than a person would -- Odenplan's bus
 * terminal and its metro station are separate, as are the two sides of a
 * road -- so the first section lists what is within a few hundred metres,
 * with the lines each of those actually carries.
 *
 * Failing that, the line runs too rarely to appear in a 20-hour window at
 * all, and no amount of looking will surface it. So it can be typed in. Such
 * a pair matches on the line number alone, because a direction code cannot be
 * known without a departure to read it from, and a guessed one would produce
 * a row that silently never fills in.
 */
export function MissingLine({
  siteId,
  onAdd,
  existing,
}: {
  siteId: number;
  onAdd: (pair: Pair) => void;
  existing: Set<string>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Card>
      <Disclosure
        open={open}
        onOpenChange={setOpen}
        summaryClassName="min-h-14 px-4 py-3"
        summary={
          <span className="text-ink flex-1 text-[15px] font-semibold">
            Saknas en linje?
          </span>
        }
      >
        {/* Mounted only once opened, so the nearby stops' departure boards are
          not fetched for everyone who never asks. */}
        {open && (
          <div className="space-y-4 px-4 pt-1 pb-4">
            <Nearby siteId={siteId} />
            <ManualAdd onAdd={onAdd} existing={existing} />
          </div>
        )}
      </Disclosure>
    </Card>
  );
}

function Nearby({ siteId }: { siteId: number }) {
  const nearby = useNearby(siteId);

  if (!nearby.data?.length) return null;

  return (
    <div>
      <h3 className="text-muted mb-2 text-[13px] font-semibold">
        Hållplatser i närheten
      </h3>
      <ul className="divide-line divide-y">
        {nearby.data.map(({ site, metres }) => (
          <li key={site.id}>
            <Link
              to={`/setup/${site.id}`}
              className="focus-visible:outline-accent flex items-center gap-2 py-2.5 focus-visible:outline-2 focus-visible:-outline-offset-2 active:opacity-60"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px]">{site.name}</span>
                <NearbyLines siteId={site.id} />
              </span>
              <span className="text-faint nums shrink-0 text-[13px]">
                {Math.round(metres)} m
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The line numbers this neighbour carries -- the point of the whole list. */
function NearbyLines({ siteId }: { siteId: number }) {
  const pairs = usePairsAtSite(siteId);

  if (pairs.isPending) return <span className="text-faint text-[13px]">…</span>;
  if (!pairs.data?.length) {
    return <span className="text-faint text-[13px]">Inga linjer</span>;
  }

  // One badge per line, not per direction: this is "is my bus over there",
  // not a second picker.
  const seen = new Map<string, (typeof pairs.data)[number]>();
  for (const p of pairs.data) if (!seen.has(p.line)) seen.set(p.line, p);

  return (
    <span className="mt-1 flex flex-wrap gap-1">
      {[...seen.values()].map((p) => (
        <LineBadge
          key={p.line}
          line={p.line}
          mode={p.mode}
          className="min-w-8 px-1 py-0.5 text-[13px]"
        />
      ))}
    </span>
  );
}

function ManualAdd({
  onAdd,
  existing,
}: {
  onAdd: (pair: Pair) => void;
  existing: Set<string>;
}) {
  const [line, setLine] = useState('');

  const designation = line.trim().toUpperCase();
  const duplicate = existing.has(`${designation}:any`);

  function add() {
    if (!designation || duplicate) return;
    onAdd({
      // BUS is a guess, and only decides the badge colour. The board replaces
      // it with the mode of a real departure as soon as one arrives, so a
      // hand-added metro line is mis-coloured until it next runs and then
      // corrects itself.
      mode: 'BUS',
      line: designation,
      directionCode: null,
      direction: 'Alla riktningar',
    });
    setLine('');
  }

  return (
    <div>
      <h3 className="text-muted mb-2 text-[13px] font-semibold">Lägg till manuellt</h3>
      <div className="flex items-end gap-2">
        <Field
          label="Linjenummer"
          value={line}
          onChange={(e) => setLine(e.target.value)}
          inputMode="numeric"
          autoComplete="off"
          placeholder="t.ex. 176"
          className="nums"
        />
        <Button onClick={add} disabled={!designation || duplicate} className="shrink-0">
          Lägg till
        </Button>
      </div>
      <p className="text-faint mt-2 text-[13px]">
        {duplicate
          ? 'Den linjen är redan tillagd.'
          : 'Visar båda riktningarna, eftersom riktningen inte går att avgöra för en linje som inte trafikerar hållplatsen just nu.'}
      </p>
    </div>
  );
}
